import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { formatCurrency, formatDate } from '@/lib/format';
import type { InstallmentStatus, ScheduledInstallment } from '@/types/fees';

const STATUS_LABEL: Record<InstallmentStatus, string> = {
  PAID: 'Paid',
  DUE: 'Due',
  OVERDUE: 'Overdue',
};

const STATUS_TONE: Record<InstallmentStatus, BadgeTone> = {
  PAID: 'success',
  DUE: 'info',
  OVERDUE: 'critical',
};

export interface InstallmentScheduleListProps {
  installments: ScheduledInstallment[];
}

/**
 * A student's installments, each with what is owed, what has been paid against
 * it and where it stands.
 *
 * The server has already laid the class's schedule against this account
 * (payments clear the oldest installment first, a concession comes off the
 * last), so this only draws it.
 */
export function InstallmentScheduleList({ installments }: InstallmentScheduleListProps) {
  return (
    <section>
      <h3 className="lbl mb-2">Installments</h3>
      <ul className="flex flex-col gap-2">
        {installments.map((installment) => (
          <li
            key={installment.uuid}
            className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-line bg-surface-2 p-3"
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink">{installment.name}</p>
              <p className="text-sm text-ink-2">Due {formatDate(installment.dueDate)}</p>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-right text-sm tabular-nums text-ink-2">
                <span className="text-ink">{formatCurrency(installment.amount)}</span>
                {Number(installment.paid) > 0 && Number(installment.balance) > 0 && (
                  <> · {formatCurrency(installment.balance)} left</>
                )}
              </p>
              <Badge tone={STATUS_TONE[installment.status]}>{STATUS_LABEL[installment.status]}</Badge>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
